import { ReactNode, useCallback } from 'react'
import { MoveEventHandler, ResizeEventHandler, useMove } from './use-movable'
import { Anchor, atAnchor, Rect } from '@st/geom'

type Props = {
  bounds: Rect
  children: ReactNode
  onMove?: MoveEventHandler
  onResize?: ResizeEventHandler
  onDoubleClick?: () => void
  selectionBorder?: boolean
  resizable?: boolean
  draggable?: boolean
  toolbar?: ReactNode
}
export function SelectedElementContainer({
  bounds,
  children,
  onMove = () => null,
  onResize = () => null,
  onDoubleClick,
  selectionBorder = true,
  resizable = false,
  draggable = true,
  toolbar
}: Props) {
  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove
  })

  return (
    <div
      ref={draggable ? ref : undefined}
      onDoubleClick={onDoubleClick}
      onPointerDown={draggable ? onPointerDown : undefined}
      onPointerMove={draggable ? onPointerMove : undefined}
      onPointerUp={draggable ? onPointerUp : undefined}
      style={{
        position: 'absolute',
        boxSizing: 'border-box',
        top: bounds.y,
        left: bounds.x,
        width: bounds.width,
        height: bounds.height,
        cursor: draggable ? 'move' : undefined,
        outline: selectionBorder ? '1px solid #3182EC' : undefined
      }}
    >
      {resizable ? <ResizeHandle anchor={Anchor.topLeft} onResize={onResize} /> : null}

      {resizable ? <ResizeHandle anchor={Anchor.topRight} onResize={onResize} /> : null}

      {resizable ? <ResizeHandle anchor={Anchor.bottomLeft} onResize={onResize} /> : null}

      {resizable ? <ResizeHandle anchor={Anchor.bottomRight} onResize={onResize} /> : null}

      {toolbar}

      {children}
    </div>
  )
}

type ResizeHandleProps = {
  anchor: Anchor
  onResize: ResizeEventHandler
}
function ResizeHandle({ anchor, onResize }: ResizeHandleProps) {
  const onMove: MoveEventHandler = useCallback(
    (e) => onResize({ ...e, anchor }),
    [onResize, anchor]
  )

  const { ref, onPointerDown, onPointerMove, onPointerUp } = useMove({
    canvasClass: 'drawing-canvas',
    onMove
  })

  const point = atAnchor({ x: 0, y: 0, width: 100, height: 100 }, anchor)
  const size = 8
  const offset = 2

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        color: '#3182EC',
        border: '1px solid #3182EC',
        boxSizing: 'border-box',
        background: 'white',
        width: size,
        height: size,
        position: 'absolute',
        left: point.x == 0 ? `${-offset}px` : `calc(${point.x}% - ${size - offset}px)`,
        top: point.y == 0 ? `${-offset}px` : `calc(${point.y}% - ${size - offset}px)`,
        zIndex: 100,
        cursor: resolveResizeCursor(anchor)
      }}
    />
  )
}

function resolveResizeCursor(anchor: Anchor) {
  if (anchor == Anchor.topLeft || anchor == Anchor.bottomRight) {
    return 'nwse-resize'
  } else if (anchor == Anchor.topRight || anchor == Anchor.bottomLeft) {
    return 'nesw-resize'
  }
  return undefined
}
